import { Users, Eye, Lightbulb, Wrench, UserCheck } from "lucide-react";
import logoIb3 from "@/assets/logo-ib-3.png";
import { useT } from "@/i18n";

const Concept = () => {
  const t = useT(
    {
      eyebrow: "Meine Haltung",
      heading: "Meine Philosophie",
      steps: [
        {
          icon: Users,
          title: "Ich starte beim Menschen",
          description: "Nicht bei einer Methode. Jeder Mensch ist individuell – deshalb beginnt meine Arbeit mit Ihrer Geschichte, Ihren Beschwerden und Ihren Zielen.",
        },
        {
          icon: Eye,
          title: "Perspektiven eröffnen",
          description: "Mögliche Zusammenhänge erkennen und unterschiedliche fachliche Perspektiven einnehmen.",
        },
        {
          icon: Lightbulb,
          title: "Möglichkeiten aufzeigen",
          description: "Therapeutische Optionen gemeinsam betrachten.",
        },
        {
          icon: Wrench,
          title: "Werkzeuge gezielt einsetzen",
          description: "Mikrobielle Einflüsse, darmassoziertes Immunsystem, biochemische Vorgänge, Methylierungen, Phytotherapie, etc. individuell verbinden.",
        },
        {
          icon: UserCheck,
          title: "Sie entscheiden",
          description: "Sie entscheiden selbst, welchen Weg Sie gehen möchten.",
        },
      ],
    },
    {
      eyebrow: "My approach",
      heading: "My philosophy",
      steps: [
        {
          icon: Users,
          title: "I start with the person",
          description: "Not with a method. Every person is unique – so my work begins with your story, your complaints and your goals.",
        },
        {
          icon: Eye,
          title: "Opening perspectives",
          description: "Recognising possible connections and taking different professional perspectives.",
        },
        {
          icon: Lightbulb,
          title: "Showing possibilities",
          description: "Considering therapeutic options together.",
        },
        {
          icon: Wrench,
          title: "Using tools purposefully",
          description: "Individually combining microbial influences, gut-associated immune system, biochemical processes, methylations, phytotherapy, etc.",
        },
        {
          icon: UserCheck,
          title: "You decide",
          description: "You decide for yourself which path you want to take.",
        },
      ],
    },
  );

  return (
    <section id="philosophie" className="py-12 md:py-16 bg-background relative overflow-hidden">
      {/* Decorative background logo */}
      <img
        src={logoIb3}
        alt=""
        aria-hidden="true"
        className="absolute left-0 top-1/2 -translate-y-1/2 w-64 md:w-96 opacity-10 pointer-events-none -translate-x-1/4"
      />
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <span className="text-primary font-medium text-sm uppercase tracking-wider">{t.eyebrow}</span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2">{t.heading}</h2>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-border -translate-x-1/2" />

            {t.steps.map((step, index) => (
              <div
                key={step.title}
                className={`relative flex items-center gap-8 mb-10 last:mb-0 ${
                  index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
              >
                {/* Content */}
                <div className={`flex-1 ${index % 2 === 0 ? "md:text-right" : "md:text-left"}`}>
                  <h3 className="text-xl font-semibold text-foreground mb-2">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>

                {/* Icon Circle */}
                <div className="relative z-10 flex-shrink-0 w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg">
                  <step.icon className="w-7 h-7" />
                </div>

                {/* Empty space for layout */}
                <div className="hidden md:block flex-1" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Concept;
